// UserNoticeModal — a message for one collector that must be acknowledged
// before the app renders. Built for the 2025 Costco checklist correction.
//
// Deliberately has no close button, no backdrop dismiss and no escape key: the
// acknowledge button is the only way out, because the point is to know the
// collector actually saw it. App.js renders this INSTEAD of the app, so there
// is nothing behind it to interact with either.
//
// Body is plain text. Blank lines separate paragraphs; a line starting with
// "* " renders as a bullet.

import { useState } from 'react';
import { supabase } from '../supabaseClient';

const fontDisplay = "'Orbitron', sans-serif";
const fontBody = "'Rajdhani', sans-serif";
const fontMono = "'Share Tech Mono', monospace";

function renderBody(body) {
  const blocks = String(body || '').split(/\n{2,}/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    const bullets = lines.filter(l => l.trim().startsWith('* '));
    if (bullets.length === lines.filter(l => l.trim()).length && bullets.length > 0) {
      return (
        <ul key={i} style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {bullets.map((b, j) => (
            <li key={j} style={{ lineHeight: 1.55 }}>{b.trim().slice(2)}</li>
          ))}
        </ul>
      );
    }
    return <p key={i} style={{ margin: 0, lineHeight: 1.55 }}>{block}</p>;
  });
}

export default function UserNoticeModal({ notice, onAcknowledged }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function acknowledge() {
    setSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('user_notices')
      .update({ acknowledged_at: new Date().toISOString() })
      .eq('id', notice.id);
    if (err) {
      // Leave the modal up. Dismissing on failure would mean the acknowledgement
      // is lost and the notice reappears next sign-in with no explanation.
      setError('That did not save. Check your connection and try again.');
      setSaving(false);
      return;
    }
    onAcknowledged();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'var(--overlay)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, overflowY: 'auto',
    }}>
      <div style={{
        background: 'var(--panel)',
        border: '1px solid var(--border-2)',
        borderTop: '2px solid var(--gold)',
        borderRadius: 4,
        maxWidth: 560, width: '100%',
        margin: 'auto',
        padding: '26px 24px 20px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {notice.title && (
          <div style={{
            fontFamily: fontDisplay, fontSize: 12, fontWeight: 700,
            letterSpacing: 2, textTransform: 'uppercase',
            color: 'var(--gold)', marginBottom: 18,
          }}>
            {notice.title}
          </div>
        )}

        <div style={{
          fontFamily: fontBody, fontSize: 16, color: 'var(--text)',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {renderBody(notice.body)}
        </div>

        {error && (
          <div style={{
            fontFamily: fontMono, fontSize: 11, color: 'var(--red-2)',
            marginTop: 16, lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        <button
          onClick={acknowledge}
          disabled={saving}
          autoFocus
          style={{
            marginTop: 24, width: '100%',
            padding: '13px 20px',
            background: saving ? 'var(--muted-2)' : 'var(--gold)',
            color: 'var(--bg)',
            border: 'none', borderRadius: 3,
            fontFamily: fontDisplay, fontSize: 13, fontWeight: 700,
            letterSpacing: 2, textTransform: 'uppercase',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : (notice.button_label || 'GOT IT')}
        </button>
      </div>
    </div>
  );
}
